/**
 * loadingMessages.ts
 * -----------------------------------------------------------------
 * Route-keyed message banks for the dynamic loading screens.
 * Each route prefix maps to an array of contextually relevant,
 * human-sounding messages. getPageMessages() picks the best bank
 * for the current pathname and shuffles it so repeat visits feel fresh.
 *
 * Used_by: SarcasticLoader (Next.js_segment loader) + LoadingOverlay
 * -----------------------------------------------------------------
 */

import type { Variant } from "@/components/skeletons/FullPageSkeleton";

type MessageBank = {
  /** Matched when pathname starts with this prefix (longer prefix wins). */
  prefix: string;
  messages: string[];
};

const MESSAGE_BANKS: MessageBank[] = [
  {
    prefix: "/ask",
    messages: [
      "Querying the academic oracle…",
      "Translating exam anxiety into answers…",
      "Summoning the Nigerian professor…",
      "Cross-referencing your lecture notes…",
      "Preparing a precise, cited response…",
      "Calibrating the AI for your course…",
      "Almost there — loading your answer…",
      "Waking up the knowledge engine…",
      "Parsing your question carefully…",
      "Consulting the academic council…",
    ],
  },
  {
    prefix: "/dashboard",
    messages: [
      "Counting your victories…",
      "Pulling up your study fortress…",
      "Waking up your desk…",
      "Loading today's mission briefing…",
      "Your stats are incoming…",
      "Checking in on your progress…",
      "Assembling your academic command centre…",
      "Good to have you back…",
      "Fetching your study_streak…",
      "Almost at your desk…",
    ],
  },
  {
    prefix: "/course-rep",
    messages: [
      "Loading your oversight dashboard…",
      "Student data incoming…",
      "Your class is waiting…",
      "Pulling the latest analytics…",
      "Checking on your students…",
      "Syncing class activity…",
      "Preparing your rep toolkit…",
      "Fetching course coverage data…",
      "Almost ready — student roster loading…",
      "Loading announcement history…",
    ],
  },
  {
    prefix: "/admin",
    messages: [
      "Initialising command node…",
      "Syncing administrative data…",
      "The platform is reporting in…",
      "Pulling platform metrics…",
      "Loading the control centre…",
      "Checking system health…",
      "Authenticating admin credentials…",
      "Fetching the operational ledger…",
      "Running integrity checks…",
      "All nodes syncing…",
    ],
  },
  {
    prefix: "/learn",
    messages: [
      "Warming up the study engine…",
      "Loading lecture materials…",
      "Opening the knowledge vault…",
      "Preparing your learning path…",
      "Fetching course content…",
      "Organising your study materials…",
      "Loading enriched notes…",
      "Almost ready to study…",
      "Your curriculum is loading…",
      "Bringing your courses online…",
    ],
  },
  {
    prefix: "/course",
    messages: [
      "Loading your course materials…",
      "Fetching lectures and notes…",
      "Preparing the course outline…",
      "Almost ready to dive in…",
      "Syncing your course progress…",
      "Loading enriched study content…",
      "Checking for new materials…",
      "Your course is loading…",
      "Opening the academic resources…",
      "Pulling everything you need…",
    ],
  },
  {
    prefix: "/community",
    messages: [
      "Gathering the study squad…",
      "Finding your people…",
      "The community is assembling…",
      "Loading the academic commons…",
      "Pulling recent threads…",
      "Connecting you to your peers…",
      "Loading campus conversations…",
      "Finding the latest discussions…",
      "Your community is waking up…",
      "Almost in the loop…",
    ],
  },
  {
    prefix: "/study-groups",
    messages: [
      "Finding your study crew…",
      "Loading study group sessions…",
      "Assembling the team…",
      "Checking group activity…",
      "Your study groups are syncing…",
      "Pulling the latest group posts…",
      "Almost ready to collaborate…",
      "Loading collaborative spaces…",
      "Your groups are coming online…",
      "Finding your people…",
    ],
  },
  {
    prefix: "/leaderboard",
    messages: [
      "Ranking the scholars…",
      "Tallying the scoreboard…",
      "Calculating who's ahead…",
      "Sorting by academic excellence…",
      "Loading the competition…",
      "Who's top of the class?…",
      "Crunching the credits…",
      "The rankings are coming…",
      "May the best scholar win…",
      "Almost there — results loading…",
    ],
  },
  {
    prefix: "/pricing",
    messages: [
      "Calculating your ROI on graduation…",
      "Unlocking the credit vault…",
      "Doing the maths…",
      "Loading your options…",
      "Every naira counts — loading plans…",
      "Preparing your study investment…",
      "The best deal is loading…",
      "Fetching plan details…",
      "Value incoming…",
      "Almost at the pricing page…",
    ],
  },
  {
    prefix: "/billing",
    messages: [
      "Checking your account…",
      "Loading your billing details…",
      "Verifying your credits…",
      "Fetching transaction history…",
      "Almost done — syncing your account…",
    ],
  },
  {
    prefix: "/pay",
    messages: [
      "Securing your payment…",
      "Preparing the transaction…",
      "Loading payment options…",
      "Almost ready to complete your order…",
      "Connecting to Paystack…",
    ],
  },
  {
    prefix: "/about",
    messages: [
      "Loading the origin story…",
      "Telling you who built this…",
      "Welcome to the inner circle…",
      "Loading our mission…",
      "The story is coming…",
      "Meet the team building your future…",
      "Built by students, for students…",
      "Fetching the vision…",
      "Almost at the about page…",
      "Loading UniUI's story…",
    ],
  },
  {
    prefix: "/faq",
    messages: [
      "Searching the knowledge base…",
      "Finding your answer…",
      "Let's figure this out together…",
      "Loading frequently asked questions…",
      "Pulling the answers…",
      "We've been asked this before…",
      "Help is on its way…",
      "Loading the FAQ database…",
      "Almost there — answers loading…",
      "Preparing the most common questions…",
    ],
  },
  {
    prefix: "/help",
    messages: [
      "Loading the help centre…",
      "Finding the right guidance…",
      "Support is on its way…",
      "Pulling the documentation…",
      "We've got you covered…",
      "Almost at the help page…",
    ],
  },
  {
    prefix: "/changelog",
    messages: [
      "Reading the commit history…",
      "Counting the features shipped…",
      "The changelog is building…",
      "Loading what's new…",
      "Reviewing the updates…",
      "The team has been busy…",
      "Loading the release notes…",
      "Fetching the latest versions…",
      "Almost ready — updates loading…",
      "Every release tells a story…",
    ],
  },
  {
    prefix: "/contact",
    messages: [
      "Connecting you to the team…",
      "Opening the support channel…",
      "Your message is almost there…",
      "Loading the contact form…",
      "We're ready to hear from you…",
      "Almost ready — form loading…",
      "The team is on standby…",
      "Preparing the support line…",
      "Loading contact options…",
      "Almost connected…",
    ],
  },
  {
    prefix: "/auth",
    messages: [
      "Preparing your workspace…",
      "Verifying your credentials…",
      "Almost in…",
      "Setting up your account…",
      "Checking your access…",
      "Welcome back — loading…",
      "Authenticating securely…",
      "One moment — signing you in…",
      "Your workspace is getting ready…",
      "Almost there…",
    ],
  },
  {
    prefix: "/login",
    messages: [
      "Preparing to sign you in…",
      "Almost in — loading…",
      "Verifying your credentials…",
      "Welcome back to UniUI…",
      "One moment…",
    ],
  },
  {
    prefix: "/register",
    messages: [
      "Setting up your account…",
      "Building your student profile…",
      "Almost ready to get started…",
      "Welcome to UniUI — loading…",
      "Preparing your workspace…",
    ],
  },
  {
    prefix: "/profile",
    messages: [
      "Loading your profile…",
      "Fetching your academic record…",
      "It's all about you…",
      "Pulling your stats…",
      "Loading your achievements…",
      "Your profile is coming together…",
      "Checking your credentials…",
      "Almost at your profile…",
      "Fetching your identity…",
      "Loading your story…",
    ],
  },
  {
    prefix: "/settings",
    messages: [
      "Loading your preferences…",
      "Fetching your settings…",
      "Personalising your experience…",
      "Almost at settings…",
      "Your preferences are loading…",
    ],
  },
  {
    prefix: "/tools",
    messages: [
      "Sharpening the study arsenal…",
      "Loading your instruments…",
      "Tools unlocked…",
      "Preparing the study toolkit…",
      "Loading your study weapons…",
      "Almost ready — tools loading…",
      "Fetching your academic instruments…",
      "Your tools are coming online…",
      "Loading the study lab…",
      "Instruments warming up…",
    ],
  },
  {
    prefix: "/past-questions",
    messages: [
      "Raiding the exam vault…",
      "Pulling from the archive…",
      "Decade of questions incoming…",
      "Loading past exam papers…",
      "Searching the question bank…",
      "Fetching previous exams…",
      "Almost in the archive…",
      "Loading the question database…",
      "Past questions are loading…",
      "Diving into the exam archive…",
    ],
  },
  {
    prefix: "/events",
    messages: [
      "Loading upcoming events…",
      "Checking the academic calendar…",
      "What's happening on campus?…",
      "Fetching event details…",
      "Loading the schedule…",
    ],
  },
  {
    prefix: "/tutoring",
    messages: [
      "Finding available tutors…",
      "Loading tutoring sessions…",
      "Connecting you with expertise…",
      "Almost ready — tutors loading…",
      "Preparing your session…",
    ],
  },
  {
    prefix: "/notifications",
    messages: [
      "Loading your notifications…",
      "Checking what you missed…",
      "Pulling your alerts…",
      "Almost ready — checking updates…",
      "Loading your inbox…",
    ],
  },
  {
    prefix: "/",
    messages: [
      "Welcome to UniUI…",
      "Loading the academic hub…",
      "Preparing your study companion…",
      "Almost home…",
      "Loading the main page…",
    ],
  },
];

/** Default sarcastic pool — used when no route prefix matches. */
const DEFAULT_MESSAGES = [
  "Bribing the course rep…",
  "Running Nigerian-professor heuristic…",
  "Calibrating academic flux…",
  "Negotiating with the server…",
  "Degaussing the hard drive…",
  "Polishing the mascot's halo…",
  "Reticulating academic splines…",
  "Aligning exam schedules with the stars…",
  "Teaching the AI sarcasm…",
  "Untangling the campus Wi-Fi…",
  "Asking the lecturer for permission…",
  "Convincing the server we're serious…",
  "Warming up the plagiarism scanner…",
  "Downloading more RAM…",
  "Mobilising the study army…",
  "Reheating leftover bytes…",
  "What are you looking at…",
  "Loading academic excellence…",
];

/** Fisher-Yates shuffle (in-place). */
function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Returns a shuffled message bank appropriate for the given pathname.
 * Longer prefix matches win (e.g. "/course-rep" beats "/course").
 */
export function getPageMessages(pathname: string): string[] {
  const matched = MESSAGE_BANKS.filter(
    (bank) =>
      pathname === bank.prefix ||
      pathname.startsWith(bank.prefix + "/") ||
      pathname.startsWith(bank.prefix),
  ).sort((a, b) => b.prefix.length - a.prefix.length);

  const bank = matched[0]?.messages ?? DEFAULT_MESSAGES;
  return shuffle([...bank]);
}

/** Cycles through messages at a given interval index. */
export function cycleMessages(messages: string[], index: number): string {
  return messages[index % messages.length] ?? messages[0] ?? "Loading…";
}

/**
 * Resolves the skeleton variant that best matches a destination route, so
 * the global LoadingSequence skeleton phase mirrors the real page structure
 * instead of always showing a generic dashboard. Longer / more-specific
 * prefixes are listed first so they win over their parent segments.
 */
const SKELETON_VARIANT_BANKS: { prefix: string; variant: string }[] = [
  // Most-specific first
  { prefix: "/admin/emails", variant: "table" },
  { prefix: "/admin/users", variant: "table" },
  { prefix: "/admin/tutors", variant: "table" },
  { prefix: "/admin/transactions", variant: "table" },
  { prefix: "/admin/tickets", variant: "table" },
  { prefix: "/admin", variant: "dashboard" },
  { prefix: "/leaderboard", variant: "table" },
  { prefix: "/billing/invoices", variant: "table" },
  { prefix: "/tools/flashcards", variant: "cards" },
  { prefix: "/user/saved", variant: "cards" },
  { prefix: "/badges", variant: "cards" },
  { prefix: "/points-shop", variant: "cards" },
  { prefix: "/profile", variant: "profile" },
  { prefix: "/u/", variant: "profile" },
  { prefix: "/events/", variant: "detail" },
  { prefix: "/events", variant: "list" },
  { prefix: "/community", variant: "list" },
  { prefix: "/view/", variant: "list" },
  { prefix: "/settings", variant: "list" },
  { prefix: "/notifications", variant: "list" },
  { prefix: "/course/", variant: "list" },
  { prefix: "/challenge", variant: "list" },
  { prefix: "/quests", variant: "list" },
  { prefix: "/departments", variant: "dashboard" },
  { prefix: "/learn", variant: "dashboard" },
  { prefix: "/courses", variant: "dashboard" },
  { prefix: "/analytics", variant: "dashboard" },
  { prefix: "/user", variant: "dashboard" },
  { prefix: "/calendar", variant: "dashboard" },
  { prefix: "/dashboard", variant: "dashboard" },
];

export function getSkeletonVariant(pathname: string): Variant {
  for (const bank of SKELETON_VARIANT_BANKS) {
    if (
      pathname === bank.prefix ||
      pathname.startsWith(bank.prefix + "/") ||
      pathname.startsWith(bank.prefix)
    ) {
      return bank.variant as Variant;
    }
  }
  return "dashboard";
}
